import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Detections, mitigations and source details for one brief
 * (GET /dashboard/rules/{attack_id}).
 *
 * State is keyed by attack id, so switching briefs never shows the previous
 * brief's rules or insight under the new headline: until the new response
 * arrives the state is 'loading'. A failed request is 'error', which the UI
 * keeps distinct from "no rules generated".
 */
export default function useBriefRules(attackId) {
  const [state, setState] = useState({ id: null, status: 'idle', data: null, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!attackId) {
      setState({ id: null, status: 'idle', data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ id: attackId, status: 'loading', data: null, error: null });
    axios
      .get(`${API}/dashboard/rules/${encodeURIComponent(attackId)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => { if (!cancelled) setState({ id: attackId, status: 'ready', data: r.data || {}, error: null }); })
      .catch((error) => { if (!cancelled) setState({ id: attackId, status: 'error', data: null, error }); });
    return () => { cancelled = true; };
  }, [attackId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // Guard against one render where attackId changed but the effect has not run yet.
  if (state.id !== attackId) return { status: attackId ? 'loading' : 'idle', data: null, error: null, reload };
  return { ...state, reload };
}
